import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSave, FiArrowLeft, FiDollarSign, FiPrinter, FiImage, FiCreditCard, FiMapPin, FiClock } from 'react-icons/fi';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import './ManageRates.css';

const ManageRates = () => {
  const navigate = useNavigate();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      const { data } = await api.get('/print-rates');
      if (data.success) {
        setRates(data.rates || []);
      } else {
        toast.error('Failed to load print rates');
      }
    } catch (err) {
      console.error('Error fetching print rates:', err);
      toast.error('Failed to load print rates');
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (key, val) => {
    setRates(prevRates =>
      prevRates.map(rate => (rate.key === key ? { ...rate, value: val } : rate))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Validate non-negative numbers
      for (const r of rates) {
        if (r.value === undefined || r.value === null || r.value === '') {
          toast.error(`${r.label} cannot be empty`);
          setSubmitting(false);
          return;
        }
        if (Number(r.value) < 0) {
          toast.error(`${r.label} cannot be negative`);
          setSubmitting(false);
          return;
        }
      }

      const formattedRates = rates.map(r => ({
        key: r.key,
        value: Number(r.value),
        label: r.label
      }));

      const { data } = await api.put('/print-rates', { rates: formattedRates });
      if (data.success) {
        toast.success('Print rates updated successfully');
        setRates(data.rates || []);
      } else {
        toast.error(data.error || 'Failed to update print rates');
      }
    } catch (err) {
      console.error('Error updating print rates:', err);
      toast.error(err.response?.data?.error || 'Failed to update print rates');
    } finally {
      setSubmitting(false);
    }
  };

  // Group rates by their service categories
  const documentRates = rates.filter(r => r.key === 'file_bw' || r.key === 'file_color');
  const passportRates = rates.filter(r => r.key.startsWith('photo_'));
  const pvcRates = rates.filter(r => r.key.startsWith('pvc_'));
  const generalRates = rates.filter(r =>
    r.key === 'service_radius_km' ||
    r.key === 'delivery_base_fee' ||
    r.key === 'delivery_free_threshold' ||
    r.key === 'delivery_charge_per_km'
  );
  const orderRates = rates.filter(r => r.key === 'order_cancellation_seconds');

  if (loading) {
    return <div className="page-container"><div className="loading-spinner" /></div>;
  }

  return (
    <div className="page-container">
      <div className="admin-header-row">
        <button onClick={() => navigate('/admin')} className="back-btn-circle">
          <FiArrowLeft />
        </button>
        <div>
          <h1 className="admin-title">Manage Rates</h1>
          <p className="admin-subtitle">Configure system-wide dynamic print pricing</p>
        </div>
      </div>

      <div className="rates-info-banner">
        <FiDollarSign className="rates-info-icon" />
        <p>Please be sure to click the <strong>"Save Pricing Rates"</strong> button at the bottom of this page once you have finished making changes.</p>
      </div>

      <form onSubmit={handleSubmit} className="manage-rates-form">
        
        {/* Document Printing Pricing Group */}
        {documentRates.length > 0 && (
          <div className="rates-group-card">
            <div className="rates-group-header">
              <FiPrinter className="group-icon doc-color" />
              <h3>Document Printing</h3>
            </div>
            <div className="rates-inputs-grid">
              {documentRates.map(rate => (
                <div className="rate-input-item" key={rate.key}>
                  <label htmlFor={rate.key}>{rate.label}</label>
                  <div className="input-currency-wrapper">
                    <span className="currency-symbol">₹</span>
                    <input
                      type="number"
                      id={rate.key}
                      value={rate.value}
                      min="0"
                      step="0.1"
                      onChange={(e) => handleRateChange(rate.key, e.target.value)}
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Print Photos Pricing Group */}
        {passportRates.length > 0 && (
          <div className="rates-group-card">
            <div className="rates-group-header">
              <FiImage className="group-icon photo-color" />
              <h3>Print Photos</h3>
            </div>
            <div className="rates-inputs-grid">
              {passportRates.map(rate => (
                <div className="rate-input-item" key={rate.key}>
                  <label htmlFor={rate.key}>{rate.label}</label>
                  <div className="input-currency-wrapper">
                    <span className="currency-symbol">₹</span>
                    <input
                      type="number"
                      id={rate.key}
                      value={rate.value}
                      min="0"
                      step="1"
                      onChange={(e) => handleRateChange(rate.key, e.target.value)}
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PVC Card Pricing Group */}
        {pvcRates.length > 0 && (
          <div className="rates-group-card">
            <div className="rates-group-header">
              <FiCreditCard className="group-icon pvc-color" />
              <h3>PVC Card Printing</h3>
            </div>
            <div className="rates-inputs-grid">
              {pvcRates.map(rate => (
                <div className="rate-input-item" key={rate.key}>
                  <label htmlFor={rate.key}>{rate.label}</label>
                  <div className="input-currency-wrapper">
                    <span className="currency-symbol">₹</span>
                    <input
                      type="number"
                      id={rate.key}
                      value={rate.value}
                      min="0"
                      step="1"
                      onChange={(e) => handleRateChange(rate.key, e.target.value)}
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* General Settings Group */}
        {generalRates.length > 0 && (
          <div className="rates-group-card">
            <div className="rates-group-header">
              <FiMapPin className="group-icon doc-color" />
              <h3>Delivery Settings</h3>
            </div>
            <div className="rates-inputs-grid">
              {generalRates.map(rate => {
                const isRadius = rate.key === 'service_radius_km';
                const isPerKm = rate.key === 'delivery_charge_per_km';
                const unitSymbol = isRadius ? 'km' : isPerKm ? '₹/km' : '₹';
                const isPrefix = !isRadius && !isPerKm;

                return (
                  <div className="rate-input-item" key={rate.key}>
                    <label htmlFor={rate.key}>{rate.label}</label>
                    <div className="input-currency-wrapper">
                      {isPrefix && <span className="currency-symbol" style={{ left: '14px', right: 'auto', fontSize: '14px' }}>₹</span>}
                      <input
                        type="number"
                        id={rate.key}
                        value={rate.value}
                        min="0"
                        step={isRadius || isPerKm ? "0.1" : "1"}
                        onChange={(e) => handleRateChange(rate.key, e.target.value)}
                        required
                        style={{
                          paddingLeft: isPrefix ? '32px' : '14px',
                          paddingRight: !isPrefix ? '54px' : '14px'
                        }}
                      />
                      {!isPrefix && <span className="currency-symbol" style={{ left: 'auto', right: '14px', fontSize: '14px' }}>{unitSymbol}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Order Settings Group */}
        {orderRates.length > 0 && (
          <div className="rates-group-card">
            <div className="rates-group-header">
              <FiClock className="group-icon order-color" />
              <h3>Order Settings</h3>
            </div>
            <div className="rates-inputs-grid">
              {orderRates.map(rate => (
                <div className="rate-input-item" key={rate.key}>
                  <label htmlFor={rate.key}>{rate.label}</label>
                  <div className="input-currency-wrapper">
                    <span className="currency-symbol">sec</span>
                    <input
                      type="number"
                      id={rate.key}
                      value={rate.value}
                      min="0"
                      step="1"
                      onChange={(e) => handleRateChange(rate.key, e.target.value)}
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <button type="submit" disabled={submitting} className="save-rates-btn">
          <FiSave /> {submitting ? 'Saving Changes...' : 'Save Pricing Rates'}
        </button>
      </form>
    </div>
  );
};

export default ManageRates;
